/**
 * M3 — universal configurator renderer for sandbox-loaded modules.
 *
 * Built-in modules (laser / 3D / bending / surface_finishes) ship
 * their own hand-coded Configurator under `processes/<name>/`. A
 * sandbox-loaded module declares its inputs in the
 * `ProcessModuleDefinition.configurator_schema` JSON blob instead;
 * this component renders those declarations as a uniform form so the
 * picker / cart / configurator shell all work without per-module FE
 * code.
 *
 * Expected schema shape — mirror of BE `AdapterManifest.pricing_inputs`:
 *
 *     {
 *       "fields": [
 *         {
 *           "key": "material_code",
 *           "kind": "material_ref" | "scalar" | "enum" | "boolean"
 *                  | "text" | "textarea",
 *           "label_cs": "Materiál",
 *           "label_en": "Material",
 *           "required": true,
 *           "unit": "mm",
 *           "enum_values": ["PLA", "PETG"],
 *           "range": {"min": 0, "max": 100, "step": 1},
 *           "visible_if": {"key": "material_code", "equals": "PLA"},
 *           "validation_rules": {"min": 0, "max": 100, "pattern": "^[A-Z]+$"}
 *         }
 *       ]
 *     }
 *
 * Richer schema knobs (`range`, `visible_if`, `validation_rules`) are
 * UX hints — the BE strategy is free to ignore them. Unknown /
 * malformed schemas degrade to a "no configurable inputs" panel.
 */

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useRemoveOperationFile,
  useUploadOperationFile,
} from "@/api/queries/processes";
import { resolveI18nText } from "@/components/admin/I18nField";
import type { ConfiguratorProps } from "@/processes/types";

interface SchemaField {
  key: string;
  kind:
    | "material_ref"
    | "scalar"
    | "enum"
    | "boolean"
    | "text"
    | "textarea"
    | "visualization"
    // KAN-1011 — sekundární souborový vstup (grafika k potisku, logo k
    // gravírování). Nahrává se na OPERACI, ne přes dílovou dropzonu.
    | "file";
  label_cs?: string;
  label_en?: string;
  required?: boolean;
  unit?: string | null;
  enum_values?: string[];
  /** Human names of the enum values, authored on the operation (KAN-1427). */
  enum_labels?: Record<string, { cs?: string; en?: string }>;
  /** Declared default — a choice that has one is never „unset". */
  default?: unknown;
  range?: { min?: number; max?: number; step?: number };
  visible_if?: { key: string; equals: unknown };
  validation_rules?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
  // kind === "file" (KAN-1011): the upload contract, so the picker can filter
  // and the size is refused before the request leaves the browser.
  accepted_file_types?: string[];
  max_upload_mb?: number;
  // KAN-1401 — pole nese hodnotu artefaktové vlastnosti; v řetězu ji dodá
  // předchůdce, takže karta kroku ho s vyplněnou hodnotou nenabízí.
  artifact_property?: string;
}

interface ConfiguratorSchema {
  fields?: SchemaField[];
}

function asNumber(value: unknown): number | undefined {
  if (typeof value !== "number") return undefined;
  return Number.isFinite(value) ? value : undefined;
}

/** ``display_unit`` — jednotka pro čtenáře (KAN-1446), „" = nezadaná. */
function displayUnitOf(field: unknown): string | undefined {
  const raw = (field as { display_unit?: unknown } | null)?.display_unit;
  return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
}

/** Non-empty trimmed string, else ``undefined``. */
function textOf(raw: unknown): string | undefined {
  return typeof raw === "string" && raw.trim() ? raw : undefined;
}

/**
 * ``value → {cs, en}`` of a choice field. Two sources, per language:
 * the author's ``enum_options`` win, and the field's ``enum_labels`` — the
 * material property registry's names, merged in by the API (KAN-1670) —
 * name whatever the author did not describe. Entries without a value are
 * ignored.
 */
function enumLabelsOf(options: unknown, registry: unknown): SchemaField["enum_labels"] {
  const out: NonNullable<SchemaField["enum_labels"]> = {};
  if (registry && typeof registry === "object" && !Array.isArray(registry)) {
    for (const [value, blob] of Object.entries(registry as Record<string, unknown>)) {
      if (!value || !blob || typeof blob !== "object") continue;
      const b = blob as Record<string, unknown>;
      out[value] = { cs: textOf(b.cs), en: textOf(b.en) };
    }
  }
  if (Array.isArray(options)) {
    for (const entry of options) {
      if (!entry || typeof entry !== "object") continue;
      const o = entry as Record<string, unknown>;
      if (typeof o.value !== "string" || !o.value) continue;
      const prev = out[o.value] ?? {};
      out[o.value] = {
        cs: textOf(o.label_cs) ?? prev.cs,
        en: textOf(o.label_en) ?? prev.en,
      };
    }
  }
  return Object.keys(out).length > 0 || Array.isArray(options) ? out : undefined;
}

function parseSchema(raw: unknown): ConfiguratorSchema {
  if (!raw || typeof raw !== "object") return {};
  const fields = (raw as { fields?: unknown }).fields;
  if (!Array.isArray(fields)) return {};
  return {
    fields: fields
      .filter((f): f is SchemaField => typeof f === "object" && f !== null)
      .map((f) => {
        const fieldObj = f as SchemaField;
        const range =
          fieldObj.range && typeof fieldObj.range === "object"
            ? {
                min: asNumber(fieldObj.range.min),
                max: asNumber(fieldObj.range.max),
                step: asNumber(fieldObj.range.step),
              }
            : undefined;
        const visibleIf =
          fieldObj.visible_if &&
          typeof fieldObj.visible_if === "object" &&
          typeof fieldObj.visible_if.key === "string"
            ? {
                key: fieldObj.visible_if.key,
                equals: fieldObj.visible_if.equals,
              }
            : undefined;
        const validation =
          fieldObj.validation_rules &&
          typeof fieldObj.validation_rules === "object"
            ? {
                min: asNumber(fieldObj.validation_rules.min),
                max: asNumber(fieldObj.validation_rules.max),
                pattern:
                  typeof fieldObj.validation_rules.pattern === "string"
                    ? fieldObj.validation_rules.pattern
                    : undefined,
              }
            : undefined;
        return {
          key: String(fieldObj.key ?? ""),
          kind: (fieldObj.kind as SchemaField["kind"]) ?? "scalar",
          label_cs: fieldObj.label_cs,
          label_en: fieldObj.label_en,
          required: Boolean(fieldObj.required),
          // KAN-1446 — ``display_unit`` je jednotka pro čtenáře, ``unit`` řídí
          // normalizaci ve vzorcích. Zobrazuje se ta první, když ji autor zadal.
          unit: displayUnitOf(fieldObj) ?? fieldObj.unit ?? null,
          enum_values: Array.isArray(fieldObj.enum_values)
            ? fieldObj.enum_values
            : undefined,
          enum_labels: enumLabelsOf(
            (fieldObj as { enum_options?: unknown }).enum_options,
            (fieldObj as { enum_labels?: unknown }).enum_labels,
          ),
          default: fieldObj.default,
          range,
          visible_if: visibleIf,
          validation_rules: validation,
          accepted_file_types: Array.isArray(fieldObj.accepted_file_types)
            ? fieldObj.accepted_file_types.map((e) => String(e))
            : undefined,
          max_upload_mb:
            typeof fieldObj.max_upload_mb === "number"
              ? fieldObj.max_upload_mb
              : undefined,
          artifact_property:
            typeof fieldObj.artifact_property === "string" &&
            fieldObj.artifact_property
              ? fieldObj.artifact_property
              : undefined,
        };
      })
      // ``visualization`` is an output slot (filled by the module's out_viz
      // view-spec via ``CustomerConfiguratorForm``), not an input — never an
      // editable control here.
      .filter((f) => f.key && f.kind !== "visualization"),
  };
}

function fieldLabel(field: SchemaField, lang: string): string {
  return (
    resolveI18nText({ cs: field.label_cs, en: field.label_en }, lang) ||
    field.key
  );
}

function evaluateVisibility(
  field: SchemaField,
  config: Record<string, unknown>,
): boolean {
  if (!field.visible_if) return true;
  return config[field.visible_if.key] === field.visible_if.equals;
}

function validateField(
  field: SchemaField,
  value: unknown,
): string | null {
  const rules = field.validation_rules;
  if (!rules) return null;
  if (rules.min !== undefined && typeof value === "number" && value < rules.min) {
    return `≥ ${rules.min}`;
  }
  if (rules.max !== undefined && typeof value === "number" && value > rules.max) {
    return `≤ ${rules.max}`;
  }
  if (rules.pattern && typeof value === "string" && value !== "") {
    try {
      const regex = new RegExp(rules.pattern);
      if (!regex.test(value)) return `pattern ${rules.pattern}`;
    } catch {
      // Bad regex in manifest — ignore so the form stays usable.
    }
  }
  return null;
}

interface DynamicConfiguratorProps
  extends ConfiguratorProps<Record<string, unknown>, unknown> {
  /**
   * Raw schema blob from `Process.configurator_schema`. The registry
   * passes it as a sidecar prop; widening `ConfiguratorProps` is out
   * of scope (built-in components ignore the field).
   */
  schema?: Record<string, unknown> | null;
  /**
   * KAN-1011 — id of the chain operation this card configures, plus the item
   * it hangs off. A secondary file is stored ON the operation, so without an
   * id there is nowhere to put it: the ``kind="file"`` fields then render as
   * a disabled note instead of a dead picker (admin preview, or the primary
   * card whose file is the part itself).
   */
  operationId?: number | null;
  itemSlug?: string | null;
  /**
   * KAN-1401 — config, který operaci vyplnil server (``config_snapshot``
   * kroku v řetězu). Pole navázané na artefakt, jehož hodnotu už dodal
   * předchůdce, se nevykreslí — zákazník ho zadal u první operace.
   */
  suppliedConfig?: Record<string, unknown> | null;
}

/** Uploaded-file reference as it is stored in ``config_snapshot[key]``. */
interface StoredFile {
  file_name?: string;
  file_key?: string;
}

function storedFileOf(value: unknown): StoredFile | null {
  if (!value || typeof value !== "object") return null;
  const name = (value as StoredFile).file_name;
  return typeof name === "string" && name ? (value as StoredFile) : null;
}

/**
 * One secondary file input (KAN-1011). The upload is its own request — the
 * file rides multipart, not the JSON config — so the control owns its
 * pending / error state instead of routing through ``onChange``.
 */
function FileFieldControl({
  field,
  label,
  value,
  operationId,
}: {
  field: SchemaField;
  label: string;
  value: unknown;
  operationId: number;
}): JSX.Element {
  const { t } = useTranslation("quote");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const upload = useUploadOperationFile();
  const remove = useRemoveOperationFile();
  // The upload answers with the whole operation, but the card's ``config``
  // prop is state held by the caller — which never saw this request. Keeping
  // the answer here is what makes the filename appear (and disappear) on the
  // same render as the click, without a redundant PATCH through onChange.
  const [own, setOwn] = useState<StoredFile | null | undefined>(undefined);
  const stored = own !== undefined ? own : storedFileOf(value);
  const busy = upload.isPending || remove.isPending;
  const accept = (field.accepted_file_types ?? []).join(",");

  const pick = (file: File | undefined) => {
    if (!file) return;
    setError(null);
    const capMb = field.max_upload_mb ?? 0;
    if (capMb > 0 && file.size > capMb * 1024 * 1024) {
      setError(
        t("dynamicConfigurator.fileTooLarge", {
          defaultValue: "Soubor je větší než {{mb}} MB.",
          mb: capMb,
        }),
      );
      return;
    }
    upload.mutate(
      { operationId, key: field.key, file },
      {
        onSuccess: (op) =>
          setOwn(storedFileOf((op.config_snapshot ?? {})[field.key])),
        onError: (e) => setError((e as Error).message),
      },
    );
  };

  return (
    <div
      className="flex flex-col gap-1 text-[12px] text-ink-3"
      data-testid={`file-field-${field.key}`}
    >
      <span>
        {label}
        {field.required ? " *" : ""}
      </span>
      {stored ? (
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] text-ink" title={stored.file_name}>
            {stored.file_name}
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setError(null);
              remove.mutate(
                { operationId, key: field.key },
                {
                  onSuccess: () => setOwn(null),
                  onError: (e) => setError((e as Error).message),
                },
              );
            }}
            className="text-[11px] text-ink-3 underline-offset-2 hover:text-ink hover:underline disabled:opacity-50"
            data-testid={`file-remove-${field.key}`}
          >
            {t("dynamicConfigurator.fileRemove", { defaultValue: "Odebrat" })}
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="h-9 rounded border border-dashed border-border bg-surface px-2 text-left text-[13px] text-ink-2 hover:border-accent hover:text-ink disabled:opacity-50"
          data-testid={`file-pick-${field.key}`}
        >
          {busy
            ? "…"
            : t("dynamicConfigurator.filePick", {
                defaultValue: "Vybrat soubor…",
              })}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept || undefined}
        hidden
        onChange={(event) => {
          pick(event.target.files?.[0]);
          // Same file twice in a row must still fire a change event.
          event.target.value = "";
        }}
        data-testid={`file-input-${field.key}`}
      />
      {field.accepted_file_types?.length ? (
        <span className="text-[11px] text-ink-4">
          {field.accepted_file_types.join(", ")}
          {field.max_upload_mb ? ` · ≤ ${field.max_upload_mb} MB` : ""}
        </span>
      ) : null}
      {error ? (
        <span className="text-[11px] text-danger" data-testid={`file-error-${field.key}`}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function DynamicConfigurator({
  config,
  onChange,
  lang,
  schema,
  operationId,
  itemSlug,
  suppliedConfig,
}: DynamicConfiguratorProps): JSX.Element {
  const { t } = useTranslation("quote");
  const parsed = parseSchema(schema);
  const fields = parsed.fields ?? [];
  const cfg = config as Record<string, unknown>;

  if (fields.length === 0) {
    return (
      <section className="rounded border border-border bg-surface-2 p-3 text-sm text-ink-2">
        {t("dynamicConfigurator.noFields", {
          defaultValue:
            "Modul nedeklaruje žádné configurator pole — kalkulace běží z defaultů.",
        })}
      </section>
    );
  }

  const set = (key: string, value: unknown) => {
    onChange({ ...config, [key]: value });
  };

  const visibleFields = fields.filter(
    (f) =>
      evaluateVisibility(f, cfg) &&
      !(
        f.artifact_property &&
        suppliedConfig != null &&
        suppliedConfig[f.key] != null
      ),
  );

  return (
    <section className="flex flex-col gap-3">
      {visibleFields.map((field) => {
        const value = cfg[field.key];
        const label = fieldLabel(field, lang);
        const required = field.required ? " *" : "";
        const validationError = validateField(field, value);

        // KAN-1011 — a secondary file lives ON the operation, so it can only
        // be uploaded once that operation exists. Without an id the field is
        // shown as a note rather than a picker that would fail on click.
        if (field.kind === "file") {
          if (operationId == null || !itemSlug) {
            return (
              <div
                key={field.key}
                className="flex flex-col gap-1 text-[12px] text-ink-3"
                data-testid={`file-field-${field.key}`}
              >
                <span>
                  {label}
                  {required}
                </span>
                <span className="text-[11px] text-ink-4">
                  {t("dynamicConfigurator.fileNeedsOperation", {
                    defaultValue:
                      "Soubor přiložíš, jakmile je krok přidán do poptávky.",
                  })}
                </span>
              </div>
            );
          }
          return (
            <FileFieldControl
              key={field.key}
              field={field}
              label={label}
              value={value}
              operationId={operationId}
            />
          );
        }

        if (field.kind === "boolean") {
          return (
            <label
              key={field.key}
              className="flex items-center gap-2 text-sm text-ink"
            >
              <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(event) => set(field.key, event.target.checked)}
                className="h-4 w-4 rounded border border-border"
              />
              <span>
                {label}
                {required}
              </span>
            </label>
          );
        }

        if (field.kind === "enum") {
          const options = field.enum_values ?? [];
          const current = typeof value === "string" ? value : "";
          // „Nezvoleno" is offered only where the declaration allows it: a
          // required choice or one with a default always carries a value
          // (KAN-1427). Choosing it removes the key — one meaning for „unset".
          const offersUnset = !field.required && field.default === undefined;
          const optionLabel = (opt: string) =>
            resolveI18nText(field.enum_labels?.[opt] ?? {}, lang) || opt;
          return (
            <label
              key={field.key}
              className="flex flex-col gap-1 text-[12px] text-ink-3"
            >
              <span>
                {label}
                {required}
              </span>
              <select
                value={current}
                onChange={(event) => {
                  const next = event.target.value;
                  if (next) {
                    set(field.key, next);
                    return;
                  }
                  const { [field.key]: _unset, ...rest } = cfg;
                  onChange(rest);
                }}
                className="h-9 rounded border border-border bg-surface px-2 text-[13px] text-ink"
                data-testid={`enum-field-${field.key}`}
              >
                {offersUnset ? (
                  <option value="">
                    {t("dynamicConfigurator.unset", { defaultValue: "Nezvoleno" })}
                  </option>
                ) : current === "" ? (
                  <option value="" disabled>
                    {t("dynamicConfigurator.choose", { defaultValue: "Vyberte…" })}
                  </option>
                ) : null}
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {optionLabel(opt)}
                  </option>
                ))}
              </select>
            </label>
          );
        }

        if (field.kind === "textarea") {
          return (
            <label
              key={field.key}
              className="flex flex-col gap-1 text-[12px] text-ink-3"
            >
              <span>
                {label}
                {required}
              </span>
              <textarea
                value={
                  value === undefined || value === null ? "" : String(value)
                }
                onChange={(event) => set(field.key, event.target.value)}
                rows={3}
                className="rounded border border-border bg-surface px-2 py-1 text-[13px] text-ink"
              />
              {validationError && (
                <span className="text-[11px] text-danger">
                  {validationError}
                </span>
              )}
            </label>
          );
        }

        // scalar / material_ref / text — single-line input. If the
        // scalar field declares ``range``, render a slider + numeric
        // input pair (so the customer can type AND drag).
        const isScalar = field.kind === "scalar";
        const isNumeric = isScalar;
        if (isScalar && field.range) {
          const rangeMin = field.range.min ?? 0;
          const rangeMax = field.range.max ?? 100;
          const rangeStep = field.range.step ?? 1;
          const currentNumber =
            typeof value === "number"
              ? value
              : value !== undefined && value !== null && value !== ""
                ? Number(value)
                : rangeMin;
          const safeNumber = Number.isFinite(currentNumber)
            ? currentNumber
            : rangeMin;
          return (
            <label
              key={field.key}
              className="flex flex-col gap-1 text-[12px] text-ink-3"
            >
              {/* KAN-1945 — no asterisk: a slider always holds a value, so
                  „required" tells the customer nothing they could miss. */}
              <span>
                {label}
                {field.unit ? ` (${field.unit})` : ""}
              </span>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={rangeMin}
                  max={rangeMax}
                  step={rangeStep}
                  value={safeNumber}
                  onChange={(event) => {
                    set(field.key, Number(event.target.value));
                  }}
                  className="flex-1"
                />
                <LocaleNumberInput
                  value={safeNumber}
                  lang={lang}
                  onChange={(next) => set(field.key, next)}
                  className="h-9 w-24 rounded border border-border bg-surface px-2 text-[13px] text-ink"
                />
              </div>
              {validationError && (
                <span className="text-[11px] text-danger">
                  {validationError}
                </span>
              )}
            </label>
          );
        }

        return (
          <label
            key={field.key}
            className="flex flex-col gap-1 text-[12px] text-ink-3"
          >
            <span>
              {label}
              {required}
              {field.unit ? ` (${field.unit})` : ""}
            </span>
            <input
              type={isNumeric ? "number" : "text"}
              value={
                value === undefined || value === null ? "" : String(value)
              }
              onChange={(event) => {
                const raw = event.target.value;
                if (isNumeric) {
                  if (raw === "") {
                    set(field.key, undefined);
                  } else {
                    const parsedNum = Number(raw);
                    set(
                      field.key,
                      Number.isFinite(parsedNum) ? parsedNum : raw,
                    );
                  }
                } else {
                  set(field.key, raw);
                }
              }}
              className="h-9 rounded border border-border bg-surface px-2 text-[13px] text-ink"
            />
            {validationError && (
              <span className="text-[11px] text-danger">
                {validationError}
              </span>
            )}
          </label>
        );
      })}
    </section>
  );
}

export default DynamicConfigurator;


/**
 * KAN-1945 — the number box next to a slider, written the page's way.
 *
 * A native ``type="number"`` draws its value in the BROWSER's locale, so an
 * English page in a Czech browser showed „0,2" under a hint saying „0.2 mm is
 * the common standard". This box formats by the page language and accepts
 * either decimal mark while typing; what the visitor types stays as typed
 * until the box loses focus.
 */
function LocaleNumberInput({
  value,
  lang,
  onChange,
  className,
}: {
  value: number;
  lang: string;
  onChange: (next: number | string | undefined) => void;
  className?: string;
}): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);
  const shown =
    draft ??
    new Intl.NumberFormat(lang, {
      useGrouping: false,
      maximumFractionDigits: 10,
    }).format(value);
  return (
    <input
      type="text"
      inputMode="decimal"
      lang={lang}
      value={shown}
      onChange={(event) => {
        const raw = event.target.value;
        setDraft(raw);
        if (raw.trim() === "") {
          onChange(undefined);
          return;
        }
        const parsed = Number(raw.trim().replace(",", "."));
        onChange(Number.isFinite(parsed) ? parsed : raw);
      }}
      onBlur={() => setDraft(null)}
      className={className}
    />
  );
}
